/** @jsxImportSource @emotion/react */
import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { Lock } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { appFeature } from 'modules/organizer/domain/interfaces/available-feature';
import React, { type ReactElement, useMemo } from 'react';
import { matchPath, useLocation, useMatch } from 'react-router-dom';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useFeature } from 'shared/hooks/use-feature';
import { theme } from 'styles/theme/theme';
import { UDText } from 'ud-ui/components';
import UDNavLink from 'ud-ui/components/link/nav-link';
import UDTippy from 'ud-ui/components/tippy';

interface IProps {
  to?: string;
  checkPath?: string;
  /**
   * Адреса (паттерны `Pages`), на которых пункт не подсвечивается, хотя `checkPath` их накрывает:
   * у «Медиа менеджера» `/manager/*` ловит и `/manager/queue` — отдельный пункт меню (IMGM-1483).
   */
  excludePaths?: string[];
  icon: ReactElement;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  onClick?: () => void;
  adminOnly?: boolean;
  hide?: boolean;
  /**
   * Фича тарифа, закрывающая раздел. Больше ничего указывать не нужно: без фичи пункт помечен
   * замком и ведёт на вкладку «Подписка» с этой фичей; пока фичи грузятся — выключен. Роут
   * раздела закрывается той же фичей в таблице `LAYOUT_ROUTES` (`router.tsx`).
   */
  feature?: appFeature;
  /** Значок справа от подписи (замок платного раздела). */
  trailing?: ReactElement;
  badge?: {
    label?: string;
    icon?: ReactElement;
    color?: string;
  };
}

interface IFeatureProps extends IProps {
  feature: appFeature;
}

function FeatureMenuItem(props: IFeatureProps) {
  const { isEnabled, isFetching } = useFeature(props.feature);
  const { t } = useLingui();

  if (!isFetching && !isEnabled) {
    // Пункт остаётся кликабельным и ведёт на тариф: выключенный пункт с подсказкой не говорит,
    // где этот раздел купить. Подсветка — по исходному адресу, а не по вкладке «Подписка».
    return (
      <MenuItemInner
        {...props}
        to={Pages.SETTINGS.builders.subscription(props.feature)}
        checkPath={props.checkPath ?? `${props.to}/*`}
        excludePaths={undefined}
        tooltip={t`Доступно в платном тарифе — нажмите, чтобы подключить`}
        trailing={<Lock size={14} aria-label={t`Платный раздел`} />}
        featureDisabled={false}
      />
    );
  }

  // Фичи ещё не пришли: куда вести, неизвестно — пункт выключен, без замка и подсказки.
  return <MenuItemInner {...props} featureDisabled={isFetching} />;
}

interface IInnerProps extends IProps {
  /** Пункт с `feature` ждёт ответа о доступных фичах. */
  featureDisabled: boolean;
}

function MenuItemInner(props: IInnerProps) {
  const { to, checkPath, excludePaths, icon, label, tooltip, disabled, onClick, adminOnly, badge, hide, featureDisabled, trailing } =
    props;
  const isAdmin = useIsAdmin();

  const effectiveDisabled = disabled || featureDisabled;
  const effectiveTooltip = featureDisabled ? undefined : tooltip;

  const url = checkPath ?? (typeof to === 'string' ? to : '');
  const matchResult = useMatch(url || '/__no_match__');
  const { pathname } = useLocation();
  const isExcluded = excludePaths?.some((path) => matchPath(path, pathname) !== null) ?? false;
  const isActive = url && !isExcluded ? matchResult : null;

  const component = useMemo(() => {
    if (!to) {
      return (
        <div
          onClick={onClick}
          style={{ height: 36, cursor: 'pointer' }}
          className={classNames('link secondary text text subhead flex items-center px-10', {
            'gap-2': !!icon,
            active: isActive,
          })}
        >
          <div className={'flex-center'}>{icon}</div>
          {label}
        </div>
      );
    }

    return (
      <UDNavLink
        to={to}
        checkPath={checkPath ?? to + '/*'}
        style={{ height: 36 }}
        className={classNames('nav-item text subhead flex items-center px-10', {
          'gap-2': !!icon,
        })}
        variant={'secondary'}
        active={isExcluded ? false : undefined}
        disabled={effectiveDisabled}
      >
        <div className={'flex-center'}>{icon}</div>
        {label}
        {trailing && <div className={'flex-center'}>{trailing}</div>}
        {badge && (
          <div
            style={{
              minWidth: 20,
              height: 20,
              borderRadius: 4,
              backgroundColor: badge.color ?? theme.colors.Blue,
            }}
            className="flex-center"
          >
            {badge.icon && (badge.icon as ReactElement)}
            {badge.label && (
              <UDText variant={'text-2'} color={theme.colors.DarkYellow}>
                {badge.label}
              </UDText>
            )}
          </div>
        )}
      </UDNavLink>
    );
  }, [badge, checkPath, effectiveDisabled, icon, isActive, isExcluded, label, onClick, to, trailing]);

  if ((adminOnly && !isAdmin) || hide) {
    return null;
  }

  if (!effectiveTooltip) {
    return component;
  }

  return (
    <UDTippy content={effectiveTooltip} placement={'bottom'}>
      <div>{component}</div>
    </UDTippy>
  );
}

function MenuItem(props: IProps) {
  if (props.feature) {
    return <FeatureMenuItem {...props} feature={props.feature} />;
  }
  return <MenuItemInner {...props} featureDisabled={false} />;
}

export default MenuItem;
